import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export const formatDate = (
  dateString: string,
  format: 'date' | 'time' | 'dateNumeric' | 'dateTime' | 'dateTimeFull' | 'dateShort' | 'default'
): string => {
  const date = new Date(dateString);

  let formattedDate;

  switch (format) {
    case 'date':
      // example: January 1, 2022
      formattedDate = new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC', // Adjust as needed
      });
      break;
    case 'time':
      formattedDate = new Intl.DateTimeFormat('en-US', { timeStyle: 'short' });
      break;
    case 'dateNumeric':
      formattedDate = new Intl.DateTimeFormat('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'UTC',
      });
      break;
    case 'dateTime':
      formattedDate = new Intl.DateTimeFormat('en-US', { dateStyle: 'short', timeStyle: 'short' });
      break;
    case 'dateTimeFull': {
      formattedDate = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' });
      const timeFormat = new Intl.DateTimeFormat('en-US', { timeStyle: 'medium' });
      return `${formattedDate?.format(date)} | ${timeFormat?.format(date)}`;
    }
    case 'dateShort':
      // example: Jan 1, 2022
      formattedDate = new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC', // Adjust as
      });
      break;
    default:
      // example: Jan 1, 2022, 12:00:00 PM
      formattedDate = new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true, // Use `false` for 24-hour format
        timeZone: 'UTC', // Adjust as needed
      });
  }
  return formattedDate?.format(date) ?? '';
};

// Renders a date as MM/DD/YYYY in UTC. Accepts either a date-only `YYYY-MM-DD`
// or a full ISO datetime (e.g. after `Dayjs.toISOString()`) — parsing via
// dayjs.utc keeps both shapes consistent and stops the time component from
// leaking into the output (a naive split('-') yields "01/01T00:00:00.000Z/2015").
// Returns the raw input unchanged if it can't be parsed.
export const formatDateAsString = (date: string): string => {
  // Guard falsy before dayjs: dayjs.utc(undefined) resolves to "now", which would
  // be a silent regression — return the input untouched instead.
  if (!date) return date;
  const parsed = dayjs.utc(date);
  return parsed.isValid() ? parsed.format('MM/DD/YYYY') : date;
};
